import {
  FunctionComponent,
  useEffect,
  useState,
  useCallback,
  useRef,
  useContext,
  ReactNode,
} from 'react'
import styled, { css } from 'styled-components'
import * as portals from 'react-reverse-portal'

import contextBlickTV from '@contexts/blickTV'

import useVideoSkeleton from '@hooks/useVideoSkeleton'

import useMount from '@hooks/useMount'
import { BlickTVContextTrackingDispatchProps } from '@widgets/Video/types/darkcove'
import SvgIcon from '@components/SvgIcon'
import t from '@i18n'

import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
} from '@utils/localStorage'

import config from '@config'
import { mobileCSS, tabletCSS, desktopCSS } from '@measures/responsive'

const { BlickTVContext } = contextBlickTV

const {
  video: {
    hideFloatingPlayerDurationInSeconds:
      hideFloatingPlayerDurationInSecondsDefault,
    storageKeyCloseFloatingPlayerTimestamp,
  },
} = config

const currentTimeInSeconds = () => Math.floor(Date.now() / 1000)

const CloseIcon = styled(SvgIcon)`
  width: 24px;
  height: 24px;
  filter: drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.25));

  ${mobileCSS(css`
    width: 4vw;
    height: 4vw;
  `)}
`

const StyledPopOutPlayer = styled.div<{ newstickerAdVisible: boolean }>`
  ${({ newstickerAdVisible }) => {
    return css`
      position: fixed;
      bottom: ${newstickerAdVisible ? '52px' : '20px'};
      right: 20px;
      border-radius: 4px;
      overflow: hidden;
      z-index: 6000000;
      box-shadow: 0 0 5.632px rgba(0, 0, 0, 0.4);

      ${desktopCSS(css`
        width: 412px;
        height: 231.75px;
      `)}

      ${tabletCSS(css`
        width: 412px;
        height: 231.75px;
      `)}

      ${mobileCSS(css`
        width: 70vw;
        padding-top: 39.375vw;
      `)}
    `
  }};
`

const StyledPlayerContainer = styled.div`
  position: relative;

  ${mobileCSS(css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `)}
`

const StyledCloseButton = styled.button`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-color: transparent;
  border: 0;
  cursor: pointer;
  position: absolute;
  z-index: 1000000;
  top: 0;
  right: 0;
  line-height: 0;
  padding: 16px;
  ${mobileCSS(css`
    padding: 16px 16px 4.26vw 4.26vw;
  `)}
`

const PopOutPlayer: FunctionComponent<{
  onDismiss: () => void
  newstickerAdShown: boolean
}> = ({ onDismiss, newstickerAdShown }) => {
  const { hasPortalNode, portalNode, isPoppedOut } = useContext(BlickTVContext)

  return hasPortalNode && isPoppedOut ? (
    <StyledPopOutPlayer
      newstickerAdVisible={newstickerAdShown}
      id="blick-tv-pop-out">
      <StyledPlayerContainer>
        <portals.OutPortal node={portalNode.current} />
      </StyledPlayerContainer>
      <StyledCloseButton onClick={onDismiss} aria-label={t('close')}>
        <CloseIcon iconName="videoplayer-close" size={21} />
      </StyledCloseButton>
    </StyledPopOutPlayer>
  ) : null
}

const BlickTVContextProvider: FunctionComponent<{ children?: ReactNode }> = ({
  children,
}) => {
  const [hasPortalNode, setHasPortalNode] = useState<boolean>(false)
  const [isPoppedOut, setIsPoppedOut] = useState<boolean>(false)
  const [hasPlayer, setHasPlayer] = useState<boolean>(false)
  const [headlineBlickTV, setHeadlineBlickTV] = useState<string>('')
  const [newstickerAdShown, setNewstickerAdShown] = useState<boolean>(false)
  const [preventAutoMount, setPreventAutoMount] = useState<boolean>(false)
  const isPoppedOutForTracking = useRef<boolean>(false)
  const auxiliaryTrackingFunction = useRef<
    (props: BlickTVContextTrackingDispatchProps) => void
  >(() => {})
  const blickTVCleanupFunction = useRef<() => void>(() => {})
  const portalNode = useRef<any>(null)
  const player = useRef<any>(null)
  const playerRef = useRef<any>(null)
  const playerIdRef = useRef<string>('')
  const [playersToBeDisposed, setPlayersToBeDisposed] = useState<Array<string>>(
    []
  )
  const popOutAllowedFunction = useRef<() => boolean>(() => true)
  const isMounted = useMount()

  const {
    hideFloatingPlayerDurationInSeconds = hideFloatingPlayerDurationInSecondsDefault,
  } = useVideoSkeleton()

  const isTimePassedSinceLastFloatingPlayerClose = useCallback(() => {
    const storageCloseFloatingPlayerTimestamp = getLocalStorageItem<number>(
      storageKeyCloseFloatingPlayerTimestamp
    )

    if (
      storageCloseFloatingPlayerTimestamp === undefined ||
      storageCloseFloatingPlayerTimestamp === null
    ) {
      return true
    } else {
      const secondsPassedSinceLastClose =
        currentTimeInSeconds() - storageCloseFloatingPlayerTimestamp
      return secondsPassedSinceLastClose > hideFloatingPlayerDurationInSeconds
    }
  }, [hideFloatingPlayerDurationInSeconds])

  const setBlickTVPlayerId = useCallback((playerId: string) => {
    playerIdRef.current = playerId
  }, [])

  const changeHeadlineBlickTV = useCallback(
    (newHeadline: string) => {
      if (isMounted()) {
        setHeadlineBlickTV(newHeadline)
      }
    },
    [isMounted]
  )

  const changeNewstickerAdVisibility = useCallback(
    (visible: boolean) => {
      if (isMounted()) {
        setNewstickerAdShown(visible)
      }
    },
    [isMounted]
  )

  useEffect(() => {
    portalNode.current = portals.createHtmlPortalNode()
    setHasPortalNode(true)
  }, [])

  const isFloating = useCallback(() => isPoppedOutForTracking.current, [])

  const setDispatchTrackingEvents = useCallback((trackingFunction: any) => {
    auxiliaryTrackingFunction.current = trackingFunction
  }, [])

  const setBlickTVCleanupFunction = useCallback(
    (functionForBlickTVCleanup: () => void) => {
      blickTVCleanupFunction.current = functionForBlickTVCleanup
    },
    []
  )

  const popOut = useCallback(() => {
    if (popOutAllowedFunction.current()) {
      if (isTimePassedSinceLastFloatingPlayerClose()) {
        setPreventAutoMount(false)
        auxiliaryTrackingFunction.current?.({ action: 'popOut' })
        setIsPoppedOut(true)
        removeLocalStorageItem(storageKeyCloseFloatingPlayerTimestamp)
      } else {
        // The "preventAutoMount" flag should be set to `true` when the floating player is manually
        // closed or cannot be shown for a specified period of time
        setPreventAutoMount(true)
      }
    }
  }, [isTimePassedSinceLastFloatingPlayerClose])

  const popIn = useCallback(() => {
    setIsPoppedOut(false)
  }, [])

  const unmount = useCallback(() => {
    setHasPlayer(false)
    setIsPoppedOut(false)
    if (playerIdRef.current) {
      setPlayersToBeDisposed((oldPlayerIds) => [
        ...oldPlayerIds,
        playerIdRef.current,
      ])
      playerIdRef.current = ''
    }
    player.current = null
    playerRef.current = null
    portalNode.current = portals.createHtmlPortalNode()
    blickTVCleanupFunction.current?.()
  }, [])

  /**
   * We dispose the old unmounted live players when a new one is created.
   */
  const disposeUnmountedLivePlayers = useCallback(() => {
    playersToBeDisposed.map((pid: string) => {
      try {
        window?.videojs?.getPlayer?.(pid)?.dispose?.()
      } catch (err) {
        // Prevent any errors from being logged in Sentry
      }
    })
    setPlayersToBeDisposed([])
  }, [playersToBeDisposed])

  const setAllowPopOutFunction = useCallback(
    (allowFunction: () => boolean) => {
      if (isMounted()) {
        popOutAllowedFunction.current = allowFunction
        if (
          !popOutAllowedFunction.current() &&
          isPoppedOutForTracking.current
        ) {
          popIn()
        }
      }
    },
    [isMounted, popOutAllowedFunction, popIn, isPoppedOutForTracking]
  )

  useEffect(() => {
    isPoppedOutForTracking.current = isPoppedOut

    playerRef.current?.current?.player?.().trigger('floating', isPoppedOut)
  }, [isPoppedOut])

  const addPlayer = useCallback(
    (playerComponent: any, refOfPlayer: any) => {
      player.current = playerComponent
      playerRef.current = refOfPlayer
      setHasPlayer(true)
      disposeUnmountedLivePlayers()
    },
    [disposeUnmountedLivePlayers]
  )

  const onDismiss = useCallback(() => {
    auxiliaryTrackingFunction.current?.({ action: 'dismiss' })
    setPreventAutoMount(true)
    popIn()
    setLocalStorageItem(
      storageKeyCloseFloatingPlayerTimestamp,
      currentTimeInSeconds()
    )
  }, [popIn])

  const stopLivePlayback = useCallback(() => {
    playerRef.current?.current?.mute?.()
    playerRef.current?.current?.pause?.()
  }, [])

  const resumeLivePlayback = useCallback(() => {
    playerRef.current?.current?.seekToLive?.()
    playerRef.current?.current?.play?.()
  }, [])

  return (
    <>
      {hasPlayer && hasPortalNode && (
        <portals.InPortal node={portalNode.current}>
          {player.current}
        </portals.InPortal>
      )}
      <BlickTVContext.Provider
        value={{
          playerRef,
          popOut,
          popIn,
          isFloating,
          setDispatchTrackingEvents,
          setBlickTVCleanupFunction,
          setBlickTVPlayerId,
          isPoppedOut,
          hasPortalNode,
          portalNode,
          hasPlayer,
          headlineBlickTV,
          changeHeadlineBlickTV,
          addPlayer,
          unmount,
          preventAutoMount,
          stopLivePlayback,
          resumeLivePlayback,
          changeNewstickerAdVisibility,
          setAllowPopOutFunction,
        }}>
        {children}
        <PopOutPlayer
          newstickerAdShown={newstickerAdShown}
          onDismiss={onDismiss}
        />
      </BlickTVContext.Provider>
    </>
  )
}

export default BlickTVContextProvider
